import { useMemo } from 'react'
import type {
  Insight,
  ParentActivity,
  ParentAttendance,
  ParentDashboard,
  ProgressSnapshot,
} from '../../../shared/api/parent'
import type { MessageRow } from '../../../shared/api/messages'
import { skillSections } from './skillSections'
import { parseSticker } from './stickerBook'
import { GAME_ART_BY_ID } from '../../../shared/ui/gameArt'
import { PARENT_COPY, type Lang } from './copy'
/* The de-emoji set plus the shared tiles this screen used to hand-draw. */
import {
  Avatar,
  ClockIcon,
  CoinIcon,
  GamepadIcon,
  MessageIcon,
  RegisterIcon,
  SparkleIcon,
  StatCard,
  StatGrid,
  StickerIcon,
  TrendUpIcon,
} from '../../../shared/components'

/**
 * ⚠️ EM DASH FOR A NUMBER THAT HAS NOT ARRIVED, NEVER A ZERO.
 *
 * A `0` on a stat card is a claim — *"your child played nothing this week"* —
 * and this screen renders before any read resolves. Showing a real-looking zero
 * during the first paint tells a parent something false about their child, which
 * is the one kind of wrong this tier cannot afford. `—` says "not yet known",
 * which is what is actually true at that moment.
 */
const PENDING = '—'

const nOrPending = (n: number | null | undefined): string =>
  typeof n === 'number' ? n.toLocaleString('en-IN') : PENDING

/**
 * An ISO timestamp → a short local date.
 *
 * ⚠️ NOT "2 days ago". A relative string has to be recomputed against a clock to
 * stay true, and this screen renders once and sits open on a phone — so "Today"
 * would still say Today tomorrow morning. A date is right whenever it is read.
 */
function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

/**
 * `YYYY-MM-DD` → "Mon", and → "Mon, 11 Aug".
 *
 * ⚠️ `T00:00:00Z` IS APPENDED AND THE FORMATTER IS PINNED TO UTC, which is the
 * rule `attendanceCalendar.formatDay` states and the reason it exists: `new
 * Date('2026-08-11')` parses as UTC midnight and then prints in the viewer's
 * zone, which is the PREVIOUS DAY for anyone west of Greenwich. A parent abroad
 * is a large part of who this tier is read by, and a weekday chip that is one
 * day out is a register that disagrees with the school's.
 */
function formatWeekday(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-IN', {
    weekday: 'short',
    timeZone: 'UTC',
  })
}

function formatDayName(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

/** Whole minutes → "4h 15m" / "45m". Zero is a real answer here, unlike a count. */
function formatMinutes(total: number): string {
  const h = Math.floor(total / 60)
  const m = total % 60
  return h > 0 ? `${String(h)}h ${String(m)}m` : `${String(m)}m`
}

export interface ParentOverviewChildItem {
  playerId: string
  name: string
  className?: string | null
}

export interface ParentOverviewScreenProps {
  lang?: Lang
  /**
   * `GET /parent/dashboard/:playerId` — the week's counts and this child's
   * skills. Null while the read is in flight or after it failed; every number
   * derived from it renders as `—` rather than as a zero.
   */
  dash?: ParentDashboard | null
  /** `GET /parent/activity/:playerId` — the day-by-day feed and its totals. */
  activity?: ParentActivity | null
  /** `GET /messages/:playerId` — what this child's teacher actually wrote. */
  messages?: MessageRow[] | null
  /** `GET /ai/insights/:playerId` — the summary, strengths and focus areas. */
  insight?: Insight | null
  /**
   * `GET /parent/children/:id/attendance?days=7` — the week's register.
   *
   * ⚠️ THE SCHOOL'S RECORD, NOT THE CHILD'S PLAY, and the one half of "was this
   * week alright" that everything else on this screen cannot answer.
   */
  attendance?: ParentAttendance | null
  /** `GET /parent/children/:id/progress` — stickers, coins and the streak. */
  progress?: ProgressSnapshot | null
  childName?: string
  activeChildId?: string
  childrenList?: ParentOverviewChildItem[]
  onOpenDetail?: () => void
  onOpenActivity?: () => void
  onOpenMessages?: () => void
  onOpenAttendance?: () => void
  onOpenAchievements?: () => void
}

export function ParentOverviewScreen({
  lang = 'en',
  /*
   * ✅ NO DEFAULT PERSON. These defaulted to "Aarav Sharma" and "Class 1 - Blue",
   * so a parent whose read had not landed — or had failed — saw a named child in
   * a named class that was not theirs. A blank is what is true before the server
   * answers, and `ParentDashboardScreen` no longer passes a stand-in either.
   */
  childName,
  activeChildId,
  childrenList,
  onOpenDetail,
  onOpenActivity,
  onOpenMessages,
  onOpenAttendance,
  onOpenAchievements,
  dash,
  activity,
  messages,
  insight,
  attendance,
  progress,
}: ParentOverviewScreenProps) {
  /* ⚠️ NAMED `c`, NOT `t`. The accuracy memo below already binds `t` to
     `activity.totals`; two `t`s in one component is the shadow nobody sees. */
  const c = PARENT_COPY[lang]
  /*
   * ✅ THE CHILD LIST IS `GET /parent/children` AND NOTHING ELSE.
   *
   * 🔴 IT USED TO FALL BACK TO THREE INVENTED CHILDREN — "Molly" and "Sahil",
   * beside the real one. A parent of one child saw two siblings that do not
   * exist, in a switcher that would have changed which child's data they were
   * reading. That is not a placeholder; it is the product telling somebody they
   * have children they do not have. An empty list is the honest state while the
   * read is in flight, and the switcher below renders nothing for it.
   */
  const displayChildren: ParentOverviewChildItem[] = childrenList ?? []

  const selectedChildObj = displayChildren.find(
    (c) => c.playerId === activeChildId
  )
  /*
   * ⚠️ THE SERVER'S NAME WINS, AND THERE IS NO INVENTED ONE UNDER IT. `dash`
   * carries the name the roster holds; the switcher's copy is the fallback only
   * because it arrives first. Neither defaults to a person.
   */
  const currentChildName = dash?.childName ?? selectedChildObj?.name ?? childName ?? ''

  /* ⚠️ EMPTY UNTIL A NAME ARRIVES, and headings that use it drop the name
     rather than greeting a blank. */
  const firstName = currentChildName.split(' ')[0] ?? ''

  /*
   * 🔴 ACCURACY IS THE ONE RATIO THIS SCREEN MAY SHOW, AND THE SERVER SENDS BOTH
   * HALVES OF IT. `ActivityTotals.answered` exists precisely *"so the client
   * never adds two numbers to divide by"*. Every other percentage the mock drew
   * — a 72% "overall progress", a 75% for Mathematics — has no field behind it:
   * `ContentSkillRow` says in the type itself that a content skill is a LEVEL and
   * *"there is deliberately no percentage field"*. So those are gone rather than
   * re-derived; `skillSections` renders what the API actually knows.
   */
  const accuracy = useMemo(() => {
    const t = activity?.totals
    if (!t || t.answered === 0) return null
    return Math.round((t.correct / t.answered) * 100)
  }, [activity])

  /* Content skills as levels, motor skills as "exercised in N of M sessions" —
     the split `skillSections` exists to keep honest. */
  const sections = useMemo(() => (dash ? skillSections(dash, 'en') : null), [dash])

  /*
   * The real feed, newest first. `ActivityDay.games` is already busiest-first
   * from the server, so the most recent day's top games are the last things this
   * child actually played — which is what "Recent Activity" claimed to be.
   */
  const recentActivities = useMemo(() => {
    const days = activity?.days ?? []
    const rows: {
      id: string
      title: string
      art: string | null
      tint: string
      category: string
      day: string
      events: number
    }[] = []
    for (let i = days.length - 1; i >= 0 && rows.length < 3; i -= 1) {
      const d = days[i]
      if (!d) continue
      for (const g of d.games) {
        if (rows.length >= 3) break
        /*
         * 🔴 THE GAME'S NAME AND ITS PLATE, NOT THE ID THE EVENTS TABLE STORES.
         *
         * This column read `g.game` — "sky-quest", "letter-lagoon",
         * "bridge-patterns" — so the panel a parent opens first printed database
         * slugs beside a generic controller glyph. `gameArt.ts` records the same
         * defect being fixed in the school reports table: *"the product's own
         * name for that game has existed in this file all along; the report
         * simply had no way to reach it."* The plates are in `public/images` and
         * were on screen nowhere in this tier.
         *
         * ⚠️ A GAME THIS BUILD DOES NOT KNOW KEEPS ITS ID AND GETS NO PLATE. An
         * older client meeting a newer game must show something true rather than
         * a broken image, and the id is at least the thing the school can be
         * asked about.
         */
        const art = GAME_ART_BY_ID[g.game as keyof typeof GAME_ART_BY_ID] as
          | (typeof GAME_ART_BY_ID)[keyof typeof GAME_ART_BY_ID]
          | undefined
        rows.push({
          id: `${d.day}-${g.game}`,
          title: art ? art.title[lang] : g.game,
          art: art ? art.art : null,
          tint: art ? art.tint : 'var(--color-primary)',
          category: g.topLevel === null ? 'Played' : `Level ${String(g.topLevel)}`,
          day: d.day,
          events: g.events,
        })
      }
    }
    return rows
  }, [activity, lang])

  /*
   * ⚠️ ONLY THE TEACHER'S SIDE, AND ONLY WHAT WAS ACTUALLY WRITTEN. The mock put
   * two invented notes from "Ms. Priya" under every child in the product. These
   * are `GET /messages/:playerId` rows with `mine === false`, newest last in the
   * thread, so the two shown are the two most recent things a real teacher said.
   */
  const teacherMessages = useMemo(
    () => (messages ?? []).filter((m) => !m.mine).slice(-2).reverse(),
    [messages]
  )

  /* ✅ The two invented arrays that stood here — three games nobody played and
     two notes "Ms. Priya" never wrote — are derived from the real feeds above. */

  /*
   * The week's register, oldest first, exactly as the server sent it.
   *
   * 🔴 NO STATE IS DERIVED HERE. `ParentAttendance.days` says it in the type:
   * three tables have to agree — absences, registers and the school's holidays —
   * and the precedence between them is a product decision the server owns. This
   * screen places seven chips and counts two of the four states.
   *
   * ⚠️ AND `NO_REGISTER` IS NOT FOLDED INTO PRESENT, which is the single
   * dishonesty the whole attendance surface is arranged to prevent. It gets its
   * own chip, its own tone and its own line under the strip.
   */
  const week = useMemo(() => {
    const days = attendance?.days ?? []
    let present = 0
    let absent = 0
    let closed = 0
    let unmarked = 0
    for (const d of days) {
      if (d.state === 'PRESENT') present += 1
      else if (d.state === 'ABSENT') absent += 1
      else if (d.state === 'HOLIDAY') closed += 1
      else unmarked += 1
    }
    return { days, present, absent, closed, unmarked }
  }, [attendance])

  /*
   * The newest stickers, as the game and level they were earned for.
   *
   * 🔴 NEVER THE RAW ID. `PlayfulUI`'s note records what this used to be
   * elsewhere in the tier: a row of pills reading `sky-quest-l2` — database
   * slugs, printed at a family, on the one screen that is about a child's
   * reward. `parseSticker` is what turns an id back into a game and a level.
   *
   * ⚠️ `parseSticker` RATHER THAN `stickerBook`, and the difference is the
   * ORDER. The book groups by game and sorts each group lowest level first —
   * *"the shelf reads as a climb"* — which is right for the shelf and wrong
   * here: this strip answers "what did they just earn", so it has to stay in the
   * order the server sent, newest last.
   */
  const recentStickers = useMemo(
    () => (progress?.stickers ?? []).slice(-3).reverse().map(parseSticker),
    [progress]
  )

  return (
    <section className="font-dash text-dash-sm text-ink space-y-6 pb-12">
      {/*
        ⚠️ THE HEADER ROW IS GONE BY REQUEST — heading, subtitle and bell.

        🔴 THIS SCREEN NOW HAS NO `<h1>`, WHICH IS THE STATE IT WAS FIXED OUT
        OF. C1 previously opened with a right-aligned row of controls and then
        a grid of numbers, so the document had no heading and a screen reader
        landed on a statistic as the first thing on the page. The panels below
        are still `<h2>`s, so the outline now starts at level 2. If a heading
        comes back, it belongs here and there must be exactly one.

        ⚠️ THE NOTIFICATION BELL WENT WITH IT. `NotificationBell` was live —
        `GET /notices/mine` and `GET /broadcasts/mine`, both scoped server-side
        from the token — so this screen no longer offers a parent any way into
        their school's notices. The feeds themselves are untouched and the
        notices route still exists; only this entry point is gone.
      */}
      {/*
        2. The four tiles.

        🔴 THEY ARE `StatCard`s NOW, AND THEY WERE FOUR HAND-DRAWN CARDS.

        Each was written out in full — `bg-indigo-50 text-indigo-600
        border-indigo-100`, `bg-emerald-50 …`, `bg-amber-50 …`, `bg-sky-50 …`
        — sixteen Tailwind-default classes, not one of them a Kalqy token, on
        the first row a parent sees. The shared kit draws the same tile for the
        school and teacher tiers, so a head teacher who also has a child at the
        school was reading two different products. It also reads label → numeral
        → caption, which is the order `design-system.md` specifies and the
        opposite of what these did.

        ⚠️ THE FOUR CAPTIONS ARE NOT INTERCHANGEABLE, AND THAT IS THE POINT.
        Two of these figures are weekly, one is all-time and one is a floor over
        a server-chosen window. A row of tiles that all said "This Week" would be
        wrong about half of itself — which is exactly what the third card used
        to do before its own note corrected it.
      */}
      <StatGrid>
        <StatCard
          label={c.gamesThisWeek}
          value={nOrPending(dash?.gamesThisWeek)}
          caption={c.captionThisWeek}
          icon={<GamepadIcon size={18} />}
          tone="indigo"
          size="sm"
        />
        {/*
          🔴 SESSIONS, NOT "ASSIGNMENTS". This product has no assignments —
          no field on `ParentDashboard`, no endpoint, no screen — and the card
          used to show an invented 9 under that word. `sessionsThisWeek` is the
          real count of times this child sat down to play.
        */}
        <StatCard
          label={c.sessionsThisWeek}
          value={nOrPending(dash?.sessionsThisWeek)}
          caption={c.captionThisWeek}
          icon={<RegisterIcon size={18} />}
          tone="leaf"
          size="sm"
        />
        {/*
          ⚠️ `coins` IS A RUNNING TOTAL, NOT A WEEKLY ONE, so the caption says
          so rather than repeating "This Week" from the card beside it. The
          field has no weekly variant.

          ⚠️ AND IT IS CALLED COINS, NOT "STARS". Coins are what the child
          collects and what the child's own screen calls them; stickers are the
          separate thing they also collect. A parent and their four-year-old
          have to be able to talk about the same number.
        */}
        <StatCard
          label={c.coins}
          value={nOrPending(dash?.coins)}
          caption={c.captionAllTime}
          icon={<CoinIcon size={18} />}
          tone="sunshine"
          size="sm"
        />
        {/*
          ⚠️ "AT LEAST", BECAUSE `ActivityDay.minutes` SAYS SO IN THE TYPE:
          *"A FLOOR, NOT A MEASUREMENT. A sitting nothing ever closed
          contributes nothing, so the screen says 'at least' wherever it prints
          this."* The mock's flat "4h 15m" made a precision claim the data
          cannot support.

          ⚠️ THE WINDOW COMES FROM THE ANSWER (`activity.windowDays`), never
          from what the screen asked for — the server clamps it.
        */}
        <StatCard
          label={c.learningTime}
          value={activity ? formatMinutes(activity.totals.minutes) : PENDING}
          caption={
            activity ? c.captionAtLeast(activity.windowDays) : undefined
          }
          icon={<ClockIcon size={18} />}
          tone="teal"
          size="sm"
        />
      </StatGrid>

      {/*
        3. The week's register, and what the child has just earned.

        🔴 THIS ROW ANSWERS THE HALF OF "was this week alright" THAT THE SCHOOL
        OWNS. Everything else on C1 comes from what the child tapped; whether
        they were in the room comes from a register a teacher saved, and it lived
        one nav item away with no trace on the screen a parent actually opens.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Card 1: Attendance this week (7 Cols) */}
        <div className="lg:col-span-7 bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card flex flex-col justify-between gap-4">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-dash-md font-extrabold text-ink">
              Attendance this week
            </h2>
            {onOpenAttendance ? (
              /*
               * 🔴 `-my-3.5 py-3.5` IS A HIT AREA, NOT SPACING, and it is on all
               * seven of this screen's "View … →" buttons for one reason: they
               * measured 19px tall on a phone. 19px is a target a thumb misses
               * more often than it finds, and these are the ONLY route from the
               * overview into the screen each card summarises — a parent who
               * cannot hit them cannot leave the dashboard.
               *
               * ⚠️ THE NEGATIVE MARGIN IS WHAT MAKES IT FREE. The padding grows
               * the border box to 47px so a thumb has something to land on; the
               * matching negative margin hands the 28px straight back to the
               * layout, so the card header is laid out exactly as it was. Sizing
               * the button with `min-h-[44px]` instead would have pushed every card
               * header 25px taller at every width, phone and laptop alike.
               */
              <button
                type="button"
                onClick={onOpenAttendance}
                className="text-dash-xs font-extrabold text-primary hover:underline cursor-pointer inline-flex items-center -my-3.5 py-3.5 gap-1"
              >
                <span>View calendar</span>
                <span aria-hidden="true">→</span>
              </button>
            ) : null}
          </div>

          {week.days.length === 0 ? (
            <p className="text-dash-xs font-semibold text-muted">
              {attendance
                ? 'No register has been taken for this class yet.'
                : 'Loading the register…'}
            </p>
          ) : (
            <>
              {/*
                ⚠️ SEVEN CHIPS, NOT A PERCENTAGE. `ParentService.attendance`
                refuses a rate for the reason it states in full: `Absence` records
                only absences, so the denominator would have to be invented. Days
                are what the database holds and days are what this shows.
              */}
              <ul className="flex flex-wrap gap-2">
                {week.days.map((d) => {
                  const tone =
                    d.state === 'ABSENT'
                      ? { bg: 'var(--color-pill-danger-bg)', fg: 'var(--color-pill-danger-fg)', line: 'var(--color-pill-danger-line)' }
                      : d.state === 'PRESENT'
                        ? { bg: 'var(--color-pill-success-bg)', fg: 'var(--color-pill-success-fg)', line: 'var(--color-pill-success-line)' }
                        : d.state === 'HOLIDAY'
                          ? { bg: 'var(--color-pill-neutral-bg)', fg: 'var(--color-grape)', line: 'var(--color-line)' }
                          : { bg: 'transparent', fg: 'var(--color-muted)', line: 'var(--color-line)' }
                  const word =
                    d.state === 'ABSENT'
                      ? c.attendanceLegendAbsent
                      : d.state === 'PRESENT'
                        ? c.attendanceLegendPresent
                        : d.state === 'HOLIDAY'
                          ? (d.holidayName ?? c.attendanceLegendHoliday)
                          : c.attendanceLegendUnknown
                  return (
                    <li
                      key={d.day}
                      title={`${formatDayName(d.day)} — ${word}`}
                      className="flex min-w-[3.25rem] flex-1 flex-col items-center gap-1 rounded-2xl border px-2 py-2.5"
                      style={{
                        background: tone.bg,
                        borderColor: tone.line,
                        /* No fill for a day nobody marked: an outline is not a
                           claim, which is the rule the month grid follows too. */
                        borderStyle: d.state === 'NO_REGISTER' ? 'dashed' : 'solid',
                      }}
                    >
                      <span className="text-[10px] font-bold uppercase tracking-wide text-muted">
                        {formatWeekday(d.day)}
                      </span>
                      <span
                        className="tabular text-dash-xs font-black"
                        style={{ color: tone.fg }}
                      >
                        {Number(d.day.slice(8, 10))}
                      </span>
                    </li>
                  )
                })}
              </ul>

              {/*
                🔴 THE COUNTS SAY ALL FOUR STATES, AND THE FOURTH IS THE ONE THAT
                KEEPS THE OTHERS HONEST. A week reading "5 present" when three of
                those days had no register saved is the single dishonesty this
                whole surface is arranged to prevent.
              */}
              <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-bold text-muted">
                <span className="text-pill-success-fg">
                  {week.present} present
                </span>
                <span className="text-pill-danger-fg">{week.absent} absent</span>
                {week.closed > 0 ? <span>{week.closed} school closed</span> : null}
                {week.unmarked > 0 ? (
                  <span>{week.unmarked} with no register taken</span>
                ) : null}
              </p>
            </>
          )}
        </div>

        {/* Card 2: What they have earned (5 Cols) */}
        <div className="lg:col-span-5 bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card flex flex-col justify-between gap-4">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-dash-md font-extrabold text-ink flex items-center gap-2">
              <StickerIcon size={17} />
              <span>Rewards</span>
            </h2>
            {onOpenAchievements ? (
              <button
                type="button"
                onClick={onOpenAchievements}
                className="text-dash-xs font-extrabold text-primary hover:underline cursor-pointer inline-flex items-center -my-3.5 py-3.5 gap-1"
              >
                <span>View all</span>
                <span aria-hidden="true">→</span>
              </button>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-line bg-canvas/40 p-3">
              <div className="text-[11px] font-bold text-muted">Stickers</div>
              <div className="tabular mt-1 text-dash-lg font-black text-ink">
                {nOrPending(progress?.stickers.length)}
              </div>
            </div>
            <div className="rounded-2xl border border-line bg-canvas/40 p-3">
              {/*
                ⚠️ COINS, AND THE SAME NUMBER THE CHILD'S OWN SCREEN SHOWS. A
                parent and their four-year-old have to be able to talk about one
                figure — which is why the tile above is not called "stars".
              */}
              <div className="text-[11px] font-bold text-muted">Coins</div>
              <div className="tabular mt-1 text-dash-lg font-black text-ink">
                {nOrPending(progress?.coins)}
              </div>
            </div>
          </div>

          {/*
            ⚠️ THE STICKER READS AS A GAME AND A LEVEL, NEVER AS ITS ID. The raw
            value is `sky-quest-l2`; printing that at a family is what
            `stickerBook` exists to stop.
          */}
          {recentStickers.length === 0 ? (
            <p className="text-dash-xs font-semibold text-muted">
              {progress
                ? 'No stickers yet. They arrive as your child finishes levels.'
                : 'Loading rewards…'}
            </p>
          ) : (
            <ul className="space-y-2">
              {recentStickers.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-3 rounded-2xl border border-line bg-canvas/40 p-2.5"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                    style={{
                      background: `color-mix(in srgb, ${s.game?.tint ?? 'var(--color-primary)'} 16%, var(--color-surface))`,
                      color: `color-mix(in srgb, ${s.game?.tint ?? 'var(--color-primary)'} 68%, var(--color-ink))`,
                    }}
                  >
                    <StickerIcon size={16} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-dash-xs font-extrabold text-ink">
                      {/* 🔴 A BUILD THAT DOES NOT KNOW THE GAME SAYS SO, rather
                          than falling through to the slug. */}
                      {s.game ? s.game.title[lang] : 'A game from another build'}
                    </span>
                    <span className="block text-[11px] font-semibold text-muted">
                      {s.level === null
                        ? 'Sticker earned'
                        : `Level ${String(s.level)} finished`}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* 4. Middle Analytics Row (2 Grid Cards) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Card 1: Learning Progress (7 Cols) */}
        <div className="lg:col-span-7 bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div>
            {/*
              ✅ THE "THIS WEEK / LAST WEEK / THIS MONTH" SELECT IS GONE, AND
              DELETING IT IS THE FIX RATHER THAN WIRING IT UP.

              It was a `<select>` whose value went into `useState` and was never
              read again — three options that drew identical numbers. A parent
              comparing this week with last week was shown one answer twice and
              told it was two, which is worse than having no control at all:
              without one, nobody believes they have changed the window.

              🔴 AND IT COULD NOT HAVE BEEN WIRED HERE. The panel underneath it
              draws content skills as LEVELS and motor skills as a share of
              sessions, both from `GET /parent/children/:id/dashboard` — a route
              that takes no window and returns cumulative facts. "Level 2 of 3
              last week" is not a quantity this product has: a level, once
              reached, is not something a child has more or less of in a given
              seven days. Adding a `days` parameter to make the control work
              would have meant inventing a windowed reading of a cumulative
              number, which is the failure `skillSections` exists to prevent.

              ⚠️ THE PARENT TIER DOES HAVE A REAL WINDOW, AND IT IS ON THE RIGHT
              SCREEN. `ProgressDetailScreen` reads `GET …/activity?days=` — day
              counts, minutes, answers, all genuinely windowed — and its own
              hint is drawn from `windowDays` in the ANSWER rather than from
              what it asked for. "View detailed progress" below is the door to
              it.
            */}
            <div className="mb-6">
              <h2 className="text-dash-md font-extrabold text-ink">
                {c.contentSkills}
              </h2>
              <p className="mt-0.5 text-dash-2xs font-semibold text-muted">
                {c.contentSkillsHint}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-8 my-2">
              {/* Circular Progress Ring graphic */}
              <div className="relative h-44 w-44 shrink-0 flex items-center justify-center">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#f1f5f9" strokeWidth="12" />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#8b5cf6"
                    strokeWidth="12"
                    strokeDasharray="251.2"
                    /* 251.2 is the full circumference; the offset is the unfilled
                       remainder. No answers yet means an empty ring, not a full
                       one. */
                    strokeDashoffset={251.2 - (251.2 * (accuracy ?? 0)) / 100}
                    strokeLinecap="round"
                  />
                </svg>

                <div className="absolute flex flex-col items-center justify-center text-center">
                  {/*
                    🔴 ACCURACY, NOT "OVERALL PROGRESS", AND THE WORDS CHANGED
                    BECAUSE THE NUMBER IS REAL NOW. There is no overall-progress
                    field anywhere in this API, and the 72% here was invented. What
                    the server does send is `correct` and `answered` — and it sends
                    `answered` explicitly *"so the client never adds two numbers to
                    divide by"*. So the ring shows the ratio that exists, and the
                    line under it shows the denominator, which is the rule
                    `skillSections` records: a bare percentage about somebody's
                    child invites "percent of what?".
                  */}
                  <span className="text-dash-xl font-black text-purple-700 leading-none">
                    {accuracy === null ? PENDING : `${String(accuracy)}%`}
                  </span>
                  <span className="text-[11px] font-bold text-muted mt-1">
                    {activity && activity.totals.answered > 0
                      ? `Correct, of ${String(activity.totals.answered)} answers`
                      : 'No answers recorded yet'}
                  </span>
                </div>
              </div>

              {/*
                🔴 THE FOUR INVENTED BARS ARE GONE, AND WHAT REPLACED THEM IS NOT
                FOUR REAL ONES. "Mathematics 75%", "Language 68%" and the rest had
                no field behind them and could not have: `ContentSkillRow` says in
                the type itself that a content skill is a LEVEL and *"there is
                deliberately no percentage field"*. Rendering one anyway would be
                a measurement claim about a child that no backend test could catch
                — the failure `AttendanceDay` refuses by the same argument.

                So content skills read as levels and motor skills read as a
                proportion with its denominator on screen. That split is what
                `skillSections` exists to enforce, and this is now its caller.
              */}
              <div className="space-y-4 flex-1 w-full">
                {sections === null ? (
                  <p className="text-dash-xs font-semibold text-muted">
                    Loading your child’s skills…
                  </p>
                ) : sections.content.length === 0 && sections.motor.length === 0 ? (
                  <p className="text-dash-xs font-semibold text-muted">
                    Nothing recorded yet. Skills appear here once your child plays.
                  </p>
                ) : (
                  <>
                    {sections.content.map((s) => (
                      <div key={s.skill} className="space-y-1">
                        <div className="flex items-center justify-between text-dash-xs font-extrabold">
                          <span>{s.label}</span>
                          {/* A LEVEL, WITH ITS CEILING. "Level 2 of 3" is the
                              sentence ADR-037 asks for; "67%" is the one it
                              forbids. */}
                          <span className="text-purple-700">
                            {s.started ? `Level ${String(s.level)} of 3` : 'Not started'}
                          </span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-purple-600"
                            style={{ width: `${String((s.level / 3) * 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                    {sections.motor.map((s) => (
                      <div key={s.skill} className="space-y-1">
                        <div className="flex items-center justify-between text-dash-xs font-extrabold">
                          <span>{s.label}</span>
                          <span className="text-purple-700">
                            {s.noData ? '—' : `${String(s.percent)}%`}
                          </span>
                        </div>
                        {/* 🔴 THE CAPTION IS NOT OPTIONAL. `skillSections` builds
                            it as "Exercised in 8 of 10 sessions" precisely so the
                            percentage is never read without what it is a
                            percentage of. */}
                        <p className="text-[10px] font-semibold text-muted">{s.caption}</p>
                        {s.noData ? null : (
                          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-purple-500"
                              style={{ width: `${String(s.percent)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-line text-center mt-4">
            <button
              type="button"
              onClick={onOpenDetail}
              className="text-dash-xs font-extrabold text-primary hover:underline cursor-pointer inline-flex items-center -my-3.5 py-3.5 gap-1.5"
            >
              <span>View Detailed Progress</span>
              <span>→</span>
            </button>
          </div>
        </div>

        {/* Card 2: this child’s insight, from GET /ai/insights/:playerId (5 Cols) */}
        <div className="lg:col-span-5 bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div>
            <h2 className="text-dash-md font-extrabold text-ink mb-4 flex items-center gap-2">
              <SparkleIcon size={17} />
              <span>{firstName ? `Insights for ${firstName}` : 'Insights'}</span>
            </h2>

            {/*
              🔴 THE SERVER'S INSIGHT, NOT TWO SENTENCES HARDCODED IN A COMPONENT.
              These cards asserted "excellent progress in Mathematics and problem
              solving" and "explore more games related to vocabulary and phonics"
              — about every child in the product, with the child's own first name
              spliced in to make it read as personal. `GET /ai/insights/:playerId`
              already returns `summary`, `strengths` and `focusAreas` for this
              child; `generatedBy` is why the attribution line below exists.
            */}
            <div className="space-y-4">
              {/* `!insight` rather than `=== null`: the prop is optional, so
                  "not passed" and "read failed" are both no-insight. */}
              {!insight ? (
                <p className="text-dash-xs font-semibold text-muted">
                  Insights appear here once there is enough play to draw on.
                </p>
              ) : (
                <>
                  {insight.summary ? (
                    <p className="text-dash-xs font-semibold text-ink/90 leading-relaxed">
                      {insight.summary}
                    </p>
                  ) : null}

                  {insight.strengths.length > 0 ? (
                    <div className="space-y-1 rounded-2xl border border-pill-success-line bg-pill-success-bg p-4">
                      <div className="flex items-center gap-2">
                        <StickerIcon size={16} />
                        <h4 className="text-dash-xs font-extrabold text-pill-success-fg">
                          Going well
                        </h4>
                      </div>
                      <ul className="list-disc pl-7 text-dash-2xs font-semibold leading-relaxed text-pill-success-fg">
                        {insight.strengths.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {insight.focusAreas.length > 0 ? (
                    <div className="space-y-1 rounded-2xl border border-pill-warning-line bg-pill-warning-bg p-4">
                      <div className="flex items-center gap-2">
                        <TrendUpIcon size={16} />
                        <h4 className="text-dash-xs font-extrabold text-pill-warning-fg">
                          Worth encouraging
                        </h4>
                      </div>
                      <ul className="list-disc pl-7 text-dash-2xs font-semibold leading-relaxed text-pill-warning-fg">
                        {insight.focusAreas.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {/* ⚠️ WHO WROTE IT, ALWAYS. An insight a parent reads as their
                      teacher's opinion, when a model produced it, is the one
                      misreading this panel must not allow. */}
                  <p className="text-[10px] font-semibold text-muted">
                    {insight.generatedBy}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-line text-center mt-4">
            <button
              type="button"
              onClick={onOpenDetail}
              className="text-dash-xs font-extrabold text-primary hover:underline cursor-pointer inline-flex items-center -my-3.5 py-3.5 gap-1.5"
            >
              <span>View Full Insights</span>
              <span>→</span>
            </button>
          </div>
        </div>
      </div>

      {/* 4. Bottom Activity & Messages Row (2 Grid Cards) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Card 1: Recent Activity (7 Cols) */}
        <div className="lg:col-span-7 bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="text-dash-md font-extrabold text-ink">Recent Activity</h2>
              <button
                type="button"
                onClick={onOpenActivity}
                className="text-dash-xs font-extrabold text-primary hover:underline cursor-pointer inline-flex items-center -my-3.5 py-3.5 gap-1"
              >
                <span>View All Activity</span>
                <span>→</span>
              </button>
            </div>

            <div className="space-y-3">
              {/*
                ⚠️ NO "Score: 92%" ROW ANY MORE, AND NOT BECAUSE IT DID NOT FIT.
                `ActivityGameRow` carries `game`, `events` and `topLevel` — there
                is no per-game score in it, and the 92% here was invented per row.
                What the feed does know is how many turns a child took and the
                highest level they reached, so that is what a row says.
              */}
              {recentActivities.length === 0 ? (
                <p className="text-dash-xs font-semibold text-muted">
                  {activity
                    ? 'Nothing played yet. Games appear here after your child’s first turn.'
                    : 'Loading recent games…'}
                </p>
              ) : (
                recentActivities.map((act) => (
                  <div
                    key={act.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-2xl border border-line bg-canvas/40 hover:bg-surface hover:border-primary/30 transition-all shadow-2xs"
                  >
                    <div className="flex items-center gap-3.5">
                      {/*
                        ⚠️ THE PLATE IS DECORATION AND SAYS SO. The game's name is
                        the `<h4>` beside it, so an `alt` here would be the same
                        words twice to a screen reader. Every plate is 3:2 and
                        safe to crop at the edges — `gameArt.ts` guarantees it —
                        which is why `object-cover` in a square is allowed.
                      */}
                      {act.art === null ? (
                        <span
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-line"
                          style={{
                            background: `color-mix(in srgb, ${act.tint} 14%, var(--color-canvas))`,
                            color: `color-mix(in srgb, ${act.tint} 68%, var(--color-ink))`,
                          }}
                        >
                          <GamepadIcon size={20} />
                        </span>
                      ) : (
                        <img
                          src={act.art}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          className="h-11 w-11 shrink-0 rounded-2xl border border-line object-cover"
                          style={{
                            background: `color-mix(in srgb, ${act.tint} 14%, var(--color-canvas))`,
                          }}
                        />
                      )}
                      <div>
                        <h4 className="text-dash-xs font-extrabold text-ink">{act.title}</h4>
                        <p className="text-[11px] font-semibold text-muted mt-0.5">
                          {act.category}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-4 text-right">
                      <div>
                        <div className="text-[11px] font-bold text-muted">{act.day}</div>
                        <div className="text-dash-xs font-black text-ink mt-0.5">
                          {act.events} {act.events === 1 ? 'turn' : 'turns'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-line text-center mt-4">
            <button
              type="button"
              onClick={onOpenActivity}
              className="text-dash-xs font-extrabold text-primary hover:underline cursor-pointer inline-flex items-center -my-3.5 py-3.5 gap-1.5"
            >
              <span>View All Games</span>
              <span>→</span>
            </button>
          </div>
        </div>

        {/* Card 2: Messages from Teacher (5 Cols) */}
        <div className="lg:col-span-5 bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="text-dash-md font-extrabold text-ink">Messages from Teacher</h2>
              <button
                type="button"
                onClick={onOpenMessages}
                className="text-dash-xs font-extrabold text-primary hover:underline cursor-pointer inline-flex items-center -my-3.5 py-3.5 gap-1"
              >
                <span>View All</span>
                <span>→</span>
              </button>
            </div>

            <div className="space-y-3">
              {teacherMessages.length === 0 ? (
                <p className="text-dash-xs font-semibold text-muted">
                  {messages
                    ? 'No messages yet. Anything your child’s teacher writes appears here.'
                    : 'Loading messages…'}
                </p>
              ) : (
                teacherMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className="p-4 rounded-2xl border border-line bg-canvas/40 space-y-2"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar name={msg.senderLabel} size={36} />
                      <div>
                        {/* 🔴 `senderLabel` IS A NAME AND NEVER A PHONE NUMBER —
                            `privacy-compliance.md` §5, which `MessageThread`
                            restates on `otherSide`. */}
                        <h4 className="text-dash-xs font-extrabold text-ink leading-tight">
                          {msg.senderLabel}
                        </h4>
                        <p className="text-[10px] font-semibold text-muted">
                          {formatWhen(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                    <p className="text-dash-xs font-medium text-ink/90 leading-relaxed bg-surface p-3 rounded-xl border border-line/60">
                      {msg.body}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-line flex justify-center mt-4">
            <button
              type="button"
              onClick={onOpenMessages}
              className="min-h-[44px] px-5 py-2 rounded-2xl border border-line bg-surface hover:bg-canvas text-dash-xs font-extrabold text-ink shadow-2xs transition-colors flex items-center gap-2 cursor-pointer"
            >
              <span>{c.messageTeacher}</span>
              <MessageIcon size={15} />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
